import { Grid, MultiSelect, SegmentedControl, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { objectiveOptions, platformOptions, positionsOptions, publisherOptions } from '../../../constants/FacebookAd';
import prettifiedLabel from '../../../constants/labelPrettifier';
import FacebookAdFormPlacementError from './FacebookAdFormPlacementError';

const defaultPlacement = {
  device_platforms: platformOptions.map((platform) => platform.value),
  publisher_platforms: publisherOptions.map((publisher) => publisher.value),
};

const filterByPlatform = (option, platforms) => {
  for (let i = 0; i < platforms.length; i++) {
    if (option.platforms.includes(platformOptions.find((platform) => platform.value === platforms[i]).id)) {
      return true;
    }
  }
  return false;
};

const generatePlacement = (objective, device_platforms, publisher_platforms) => {
  let placement = {
    device_platforms,
    publisher_platforms,
  };

  let platforms = placement.device_platforms;
  let publishers = placement.publisher_platforms;

  for (let i = 0; i < publishers.length; i++) {
    placement = {
      ...placement,
      [`${publishers[i]}_positions`]: positionsOptions[`${publishers[i]}`]
        .filter((publisher) => publisher.objectives.find((option) => option === objective))
        .filter((option) => filterByPlatform(option, platforms))
        .map((option) => option.value),
    };
  }
  return placement;
};

const FacebookAdFormPlacement = ({ facebookAd, setFacebookAd }) => {
  const objectiveOptionId = objectiveOptions.find((option) => facebookAd.objective === option.value).id;

  const [enabled, setEnabled] = useState(Object.keys(facebookAd.placement).length !== 0 || false);
  const [adPlacement, setAdPlacement] = useState(
    Object.keys(facebookAd.placement).length === 0
      ? generatePlacement(objectiveOptionId, defaultPlacement.device_platforms, defaultPlacement.publisher_platforms)
      : facebookAd.placement
  );

  useEffect(() => {
    if (!facebookAd.id) {
      const placement = generatePlacement(
        objectiveOptionId,
        defaultPlacement.device_platforms,
        publisherOptions
          .filter((option) => option.objectives.find((option) => option === objectiveOptionId))
          .map((option) => option.value)
      );
      setFacebookAd((prev) => ({ ...prev, placement: enabled ? { ...placement } : {} }));
      setAdPlacement(placement);
    }
  }, [objectiveOptionId, setAdPlacement, facebookAd.id, setFacebookAd, enabled]);

  const handleEnable = (value) => {
    const placement = generatePlacement(
      objectiveOptionId,
      defaultPlacement.device_platforms,
      publisherOptions
        .filter((option) => option.objectives.find((option) => option === objectiveOptionId))
        .map((option) => option.value)
    );

    setEnabled(value);
    setFacebookAd({
      ...facebookAd,
      placement: value ? { ...placement } : {},
    });
    setAdPlacement(placement);
  };

  const filterByObjective = (option) => {
    return option.objectives.find((option) => option === objectiveOptionId);
  };

  const setPlacementValues = (placement) => {
    setAdPlacement(placement);
    setFacebookAd({ ...facebookAd, placement });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'device_platforms') {
      const publishers = publisherOptions
        .filter((option) => filterByPlatform(option, value))
        .map((option) => option.value);

      setPlacementValues(generatePlacement(objectiveOptionId, value, publishers));

      return;
    }

    if (name === 'publisher_platforms') {
      setPlacementValues(generatePlacement(objectiveOptionId, adPlacement.device_platforms, value));
      return;
    }

    setPlacementValues({ ...adPlacement, [name]: value });
  };

  const platformSelectElement = (
    <Grid>
      <Grid.Col span={3}></Grid.Col>
      <Grid.Col span={2}>
        <Text size="sm" fw={500}>Device platform</Text>
      </Grid.Col>
      <Grid.Col span={7}>
        <MultiSelect
          disabled={facebookAd.id}
          data={platformOptions}
          onChange={(value) => {
            handleChange({ target: { name: 'device_platforms', value } });
          }}
          value={adPlacement.device_platforms}
          placeholder="Select ad device platform ..."
        />
        <Text size="sm" c="dimmed">This controls the device platform for your ad.</Text>
      </Grid.Col>
    </Grid>
  );

  console.log(publisherOptions.filter((option) => filterByPlatform(option, adPlacement.device_platforms)));

  const publisherSelectElement = (
    <Grid>
      <Grid.Col span={3}></Grid.Col>
      <Grid.Col span={2}>
        <Text size="sm" fw={500}>Publisher platform</Text>
      </Grid.Col>
      <Grid.Col span={7}>
        <MultiSelect
          disabled={facebookAd.id}
          data={publisherOptions.filter((option) => filterByPlatform(option, adPlacement.device_platforms))}
          onChange={(value) => {
            handleChange({ target: { name: 'publisher_platforms', value } });
          }}
          value={adPlacement.publisher_platforms}
          placeholder="Select ad publisher platform ..."
        />
        <Text size="sm" c="dimmed">This controls the publisher platform for your ad.</Text>
      </Grid.Col>
    </Grid>
  );

  const positionElements = adPlacement.publisher_platforms.map((publisher, index) => {
    return (
      <Grid key={index}>
        <Grid.Col span={3}></Grid.Col>
        <Grid.Col span={2}>
          <Text size="sm" fw={500}>{prettifiedLabel(`${publisher} Positions`)}</Text>
        </Grid.Col>
        <Grid.Col span={7}>
          <MultiSelect
            disabled={facebookAd.id}
            data={positionsOptions[`${publisher}`]
              .filter(filterByObjective)
              .filter((option) => filterByPlatform(option, adPlacement.device_platforms))}
            onChange={(value) => {
              handleChange({ target: { name: `${publisher}_positions`, value } });
            }}
            value={adPlacement[`${publisher}_positions`]}
            placeholder={`Select ad ${publisher} positions ...`}
          />
          <Text size="sm" c="dimmed">This controls the {`${publisher}`} placements for your ad.</Text>
        </Grid.Col>
      </Grid>
    );
  });

  return (
    <>
      <Grid>
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Ad placement
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <SegmentedControl
            value={String(enabled)}
            disabled={!!facebookAd.id}
            onChange={(value) => handleEnable(value === 'true')}
            data={[
              { label: 'Advantage+', value: 'false' },
              { label: 'Manual placements', value: 'true' },
            ]}
          />
          <Text size="sm" c="dimmed">
            <b>Advantage+</b> placements will maximize your budget and help show your ads to more people.
            <br />
            <b>Manually placements</b> choose the places to show your ad. The more placements you select, the more
            opportunities you'll have to reach your target audience and achieve your business goals.
          </Text>
        </Grid.Col>
      </Grid>
      {enabled ? (
        <>
          <FacebookAdFormPlacementError adPlacement={adPlacement} />
          {platformSelectElement}
          {publisherSelectElement}
          {positionElements}
        </>
      ) : null}
    </>
  );
};

export default FacebookAdFormPlacement;
